import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BPWizardService, IBPProfileData } from './bp-wizard.service';
import { IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import * as _ from 'underscore';
import { Subscription } from 'rxjs';
import { StateService, Transition, TransitionService } from '@uirouter/angular';
import { BPProfilesDataService, IBPWizardProgress } from '../bp-profiles-data.service';
import { BPProfilesService } from '../bp-profiles.service';
import { IBPProfile } from '../../../../type_definitions/datatypes';

import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { util } from '../../../shared/util';
import { STATE_BPWIZARD_THANKYOU } from '../../../shared/app-states';
import { IProfileAttributeResponse } from '../profile-attributes/bp-profiles-attributes.service';
import { BPProfilesTCDataService } from '../advanced/tc/bp-profiles-tc-data.service';

export const PROFILE_ATTRIBUTES_FORM:string = 'profileAttributesForm';

@Injectable()
export abstract class BaseBPWizardFormComponent implements OnInit, OnDestroy {
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:IBPProfileData | any;
  public fields:IExtraFieldConfigurationObject[];
  public stepComplete:boolean;
  public loading:boolean;

  protected labelClass:string = 'col-sm-3 col-md-3';
  protected valueClass:string = 'col-sm-9 col-md-5';
  protected formType:string;

  private onStateExitHandler:Function;
  private onSaveSubscription:Subscription;
  private valueChangesSubscription:Subscription;

  protected constructor(
    protected transitionService:TransitionService,
    protected bpWizardService:BPWizardService,
    protected bpProfilesService?:BPProfilesService,
    protected bpProfilesDataService?:BPProfilesDataService,
    protected validateService?:GaValidateService,
    protected stateService?:StateService,
    protected bpProfilesTCDataService?:BPProfilesTCDataService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.stepComplete = this.bpWizardService.isCurrentStepComplete();

    // load data
    this.loading = true;
    this.loadData()
      .then(this.onDataLoaded.bind(this))
      .finally(() => {
        this.loading = false;
      });

    // register listener
    this.listen();
  }

  public listen():void {
    this.onStateExitHandler = this.transitionService.onExit({}, (transition:Transition):Promise<void> => {
      if (transition.from().name === transition.to().name || transition.params('from').profileId !== transition.params('to').profileId) {
        return Promise.resolve();
      }
      // wait for this promise to be resolved before to do the transition to the new state
      return this.onSave();
    });

    this.valueChangesSubscription = this.form.valueChanges.subscribe(() => {
      this.bpWizardService.model = _.extend(this.bpWizardService.model, this.model);
      this.bpWizardService.emitSaveButtonEnabled(this.isFormValuesChange);
    });

    this.onSaveSubscription = this.bpWizardService.onSave.subscribe(this.onSave);
  }

  public loadData():Promise<any> {
    return Promise.resolve();
  }

  public onDataLoaded():void {
    this.model = this.bpWizardService.model;
    this.fields = this.createFields();
    this.fillFields();
  }

  public get isFormValuesChange():boolean {
    return this.form.dirty && !_.isEmpty(util.diff(this.bpWizardService.backupModel, this.bpWizardService.model));
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    return [];
  }

  protected fillFields():void { /**/ }

  protected setSaveBusy(isBusy:boolean) {
    this.bpWizardService.emitUpdateWizardButtonStatus(isBusy);
  }

  protected onSave = (isSubmit?:boolean):Promise<any> => {
    return this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        let savePromise:Promise<void> = Promise.resolve();
        this.setSaveBusy(true);

        // we only save when we have different model
        if (this.isFormValuesChange) {
          if (this.formType === PROFILE_ATTRIBUTES_FORM) {
            savePromise = <Promise<void>>this.bpProfilesTCDataService.saveTC(this.bpProfilesService.activeProfile?.id, this.bpWizardService.createProfileAttributesRequest(this.model))
              .then((data:IProfileAttributeResponse) => {
                this.bpWizardService.tc = data;
                this.bpWizardService.setWizardModel();
              });
          } else {
            savePromise = <Promise<void>>this.bpProfilesDataService.updateBPProfileDetail(this.bpProfilesService.activeProfile?.id, this.bpWizardService.createBPProfileRequestData())
              .then((profileDetail:IBPProfile) => {
                this.bpProfilesService.profileDetail = profileDetail;
                this.bpWizardService.setWizardModel(profileDetail);
              });
          }
        }

        return savePromise
          .then(this.getProgress.bind(this))
          .then(() => {
            this.bpWizardService.emitSaveButtonEnabled(false);
            if (isSubmit) {
              this.stateService.go(STATE_BPWIZARD_THANKYOU, { profileId: this.bpProfilesService.activeProfile?.id });
            }
          })
          .catch(() => {
            this.bpWizardService.emitSaveButtonEnabled(true);
          })
          .finally(() => {
            this.setSaveBusy(false);
          });
      })
      .catch(util.noop);
  };

  protected getProgress():Promise<void> {
    return this.bpProfilesDataService.getProfileProgress(this.bpProfilesService.activeProfile?.id)
      .then((wizardProgress:IBPWizardProgress) => {
        this.bpWizardService.progress = wizardProgress;
      });
  }

  public ngOnDestroy():void {
    this.onStateExitHandler();
    this.onSaveSubscription?.unsubscribe();
    this.valueChangesSubscription?.unsubscribe();
  }
}